class ChatbotWidget extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.isLoading = false;
    this.messages = [
      { role: 'bot', content: 'Hi! I\'m the Pillowy assistant. Ask me anything about our pillows, shipping, returns, or anything else.' }
    ];
    this.suggestions = [
      'How long does shipping take?',
      'What\'s your return policy?',
      'Which pillow is best for side sleepers?',
      'How do I care for my pillow?',
    ];
  }

  connectedCallback() {
    this.render();
    this.panel = this.querySelector('.chatbot__panel');
    this.input = this.querySelector('.chatbot__input');
    this.messagesContainer = this.querySelector('.chatbot__messages');
    this.sendBtn = this.querySelector('.chatbot__send');
    this.suggestionsContainer = this.querySelector('.chatbot__suggestions');

    this.querySelector('.chatbot__toggle').addEventListener('click', () => this.toggle());
    this.querySelector('.chatbot__close').addEventListener('click', () => this.close());
    this.querySelector('.chatbot__form').addEventListener('submit', (e) => {
      e.preventDefault();
      this.sendMessage();
    });
    this.suggestionsContainer.addEventListener('click', (e) => {
      const chip = e.target.closest('.chatbot__suggestion');
      if (chip) this.sendMessage(chip.textContent);
    });
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    });

    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    this.panel.classList.add('is-open');
    if (window.innerWidth < 750) document.body.classList.add('overflow-hidden');
    setTimeout(() => this.input.focus(), 200);
    this.scrollToBottom();
  }

  close() {
    this.isOpen = false;
    this.panel.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
  }

  async sendMessage(textOverride) {
    const text = (textOverride ?? this.input.value).trim();
    if (!text || this.isLoading) return;

    this.input.value = '';
    this.suggestionsContainer.hidden = true;
    this.messages.push({ role: 'user', content: text });
    this.isLoading = true;
    this.sendBtn.disabled = true;
    this.renderMessages();
    this.renderTyping();

    try {
      const history = this.messages.slice(1, -1).map((m) => ({
        role: m.role === 'bot' ? 'model' : 'user',
        content: m.content,
      }));

      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, history }),
      });

      if (!res.ok) throw new Error('Request failed');
      const data = await res.json();
      this.messages.push({ role: 'bot', content: data.response });
    } catch {
      this.messages.push({ role: 'bot', content: 'Sorry, something went wrong. Please try again or email us at hello@pillowy.com.' });
    } finally {
      this.isLoading = false;
      this.sendBtn.disabled = false;
      this.renderMessages();
    }
  }

  renderMessages() {
    const html = this.messages
      .map((m) => `<div class="chatbot__message chatbot__message--${m.role}">${this.escape(m.content)}</div>`)
      .join('');
    this.messagesContainer.innerHTML = html;
    this.scrollToBottom();
  }

  renderTyping() {
    const indicator = document.createElement('div');
    indicator.className = 'chatbot__typing';
    indicator.innerHTML = '<span class="chatbot__typing-dot"></span><span class="chatbot__typing-dot"></span><span class="chatbot__typing-dot"></span>';
    this.messagesContainer.appendChild(indicator);
    this.scrollToBottom();
  }

  scrollToBottom() {
    requestAnimationFrame(() => {
      this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
    });
  }

  escape(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');
  }

  render() {
    this.innerHTML = `
      <button class="chatbot__toggle" aria-label="Open chat" aria-expanded="false">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2v10z"/>
        </svg>
      </button>
      <div class="chatbot__panel" role="dialog" aria-label="Pillowy chat support">
        <div class="chatbot__header">
          <div>
            <div class="chatbot__header-title">Pillowy Support</div>
            <div class="chatbot__header-subtitle">Ask us anything</div>
          </div>
          <button class="chatbot__close" aria-label="Close chat">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="chatbot__messages">
          ${this.messages.map((m) => `<div class="chatbot__message chatbot__message--${m.role}">${this.escape(m.content)}</div>`).join('')}
        </div>
        <div class="chatbot__suggestions">
          ${this.suggestions.map((s) => `<button type="button" class="chatbot__suggestion">${this.escape(s)}</button>`).join('')}
        </div>
        <form class="chatbot__form">
          <input
            class="chatbot__input"
            type="text"
            placeholder="Ask about products, shipping, returns…"
            autocomplete="off"
            maxlength="500"
          />
          <button class="chatbot__send" type="submit" aria-label="Send message">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
            </svg>
          </button>
        </form>
      </div>
    `;
  }
}

customElements.define('chatbot-widget', ChatbotWidget);
